import { GetStaticProps, GetStaticPaths } from "next";
import { Fragment, useMemo } from "react";
import dynamic from "next/dynamic";
import type { Page, PageComponent } from "@/services/page";
import { removeDuplicatedTemplateSeo } from "@/lib/pagePropsOptimizer";
import { pageService } from "@/services/page/page.service";
import { blogService } from "@/services/page/blog.service";
import { galleryService } from "@/services/page/gallery.service";
import { componentService } from "@/services/page/component.service";
import LoadingBar from "@/components/ui/LoadingBar";
import HtmlLinksLoader from "@/components/ui/HtmlLinksLoader";
import { ComponentData } from "@/types/components";
import { sanitizeHTML } from "@/lib/utils";
import { NOT_FOUND_REVALIDATE_SECONDS, PAGE_REVALIDATE_SECONDS } from "@/lib/constants";

interface PageProps {
  page: Page;
  preview?: boolean;
}

const withTiming = async <T,>(label: string, fn: () => Promise<T>) => {
  const start = Date.now();
  const result = await fn();
  const duration = Date.now() - start;
  console.log(`[perf] ${label}: ${duration}ms`);
  return result;
};




// Static — render on every page or LCP hero elements
import Header from "@/components/Header/Header";
import Footer from "@/components/Footer/Footer";
import HomeHero from "@/components/HomeHero/HomeHero";
import HeroTitleTextCta from "@/components/HeroTitleTextCta/HeroTitleTextCta";
import HeroMainText from "@/components/HeroMainText/HeroMainText";
import HarleyStreetFaceliftHero from "@/components/HarleyStreetFaceliftHero/HarleyStreetFaceliftHero";

// Dynamic — SSR enabled (code-split per page visit)
const AboutSection = dynamic(() => import("@/components/AboutSection/AboutSection"));
const AboutNavigationSection = dynamic(() => import("@/components/AboutNavigationSection/AboutNavigationSection"));
const MedicalAssociationsSection = dynamic(() => import("@/components/MedicalAssociationsSection/MedicalAssociationsSection"));
const FournColumnsSection = dynamic(() => import("@/components/FournColumnsSection/FournColumnsSection"));
const DrDeSilvaOverviewSection = dynamic(() => import("@/components/DrDeSilvaOverviewSection/DrDeSilvaOverviewSection"));
const ArtistrySection = dynamic(() => import("@/components/ArtistrySection/ArtistrySection"));
const ArtistryBackedByScience = dynamic(() => import("@/components/ArtistryBackedByScience/ArtistryBackedByScience"));
const PhilanthropyFoundation = dynamic(() => import("@/components/PhilanthropyFoundation/PhilanthropyFoundation"));
const BookPrivateConsultation = dynamic(() => import("@/components/BookPrivateConsultation/BookPrivateConsultation"));
const PhilanthropySection = dynamic(() => import("@/components/PhilanthropySection/PhilanthropySection"));
const SpecialtyTrainingSection = dynamic(() => import("@/components/SpecialtyTrainingSection/SpecialtyTrainingSection"));
const ConsultationCTA = dynamic(() => import("@/components/ConsultationCTA/ConsultationCTA"));
const TestimonialSection = dynamic(() => import("@/components/TestimonialSection/TestimonialSection"));
const TestimonialSectionGrey = dynamic(() => import("@/components/TestimonialSectionGrey/TestimonialSectionGrey"));
const AboutDrDeSilvaSection = dynamic(() => import("@/components/AboutDrDeSilvaSection/AboutDrDeSilvaSection"));
const AwardsAndRecognitionSection = dynamic(() => import("@/components/AwardsAndRecognitionSection/AwardsAndRecognitionSection"));
const PageSectionNavigation = dynamic(() => import("@/components/PageSectionNavigation/PageSectionNavigation"));
const WhatIsDeepPlaneFacelift = dynamic(() => import("@/components/WhatIsDeepPlaneFacelift/WhatIsDeepPlaneFacelift"));
const FacialLayersSection = dynamic(() => import("@/components/FacialLayersSection/FacialLayersSection"));
const ConsultationForm = dynamic(() => import("@/components/ConsultationForm/ConsultationForm"));
const FacialSurgeryResultsShowcase = dynamic(() => import("@/components/FacialSurgeryResultsShowcase/FacialSurgeryResultsShowcase"));
const Vfr7DeepPlaneFacelift = dynamic(() => import("@/components/Vfr7DeepPlaneFacelift/Vfr7DeepPlaneFacelift"));
const VfrSevenFaceliftSteps = dynamic(() => import("@/components/VfrSevenFaceliftSteps/VfrSevenFaceliftSteps"));
const InThePress = dynamic(() => import("@/components/InThePress/InThePress"));
const CandidateDeepPlane = dynamic(() => import("@/components/CandidateDeepPlane/CandidateDeepPlane"));
const WhatToExpect = dynamic(() => import("@/components/WhatToExpect/WhatToExpect"));
const FrequentlyQuestions = dynamic(() => import("@/components/FrequentlyQuestions/FrequentlyQuestions"));
const DeepPlaneVsStandard = dynamic(() => import("@/components/DeepPlaneVsStandard/DeepPlaneVsStandard"));
const AboutTheAuthor = dynamic(() => import("@/components/AboutTheAuthor/AboutTheAuthor"));
const TitleButtonSection = dynamic(() => import("@/components/TitleButtonSection/TitleButtonSection"));
const HeadingLinkSection = dynamic(() => import("@/components/HeadingLinkSection/HeadingLinkSection"));
const RequestCall = dynamic(() => import("@/components/RequestCall/RequestCall"));
const WelcomeToPractice = dynamic(() => import("@/components/WelcomeToPractice/WelcomeToPractice"));
const FacialSurgeon = dynamic(() => import("@/components/FacialSurgeon/FacialSurgeon"));
const AcademicPublishedExpertise = dynamic(() => import("@/components/AcademicPublishedExpertise/AcademicPublishedExpertise"));
const EducationQualificationLeadership = dynamic(() => import("@/components/EducationQualificationLeadership/EducationQualificationLeadership"));
const WhatWeOffer = dynamic(() => import("@/components/WhatWeOffer/WhatWeOffer"));
const VerticalFaceRestore7 = dynamic(() => import("@/components/VerticalFaceRestore7/VerticalFaceRestore7"));
const PatientsSaying = dynamic(() => import("@/components/PatientsSaying/PatientsSaying"));
const InterestedInForm = dynamic(() => import("@/components/InterestedInForm/InterestedInForm"));
const BlogPreview = dynamic(() => import("@/components/BlogPreview/BlogPreview"));
const QuestionsSections = dynamic(() => import("@/components/QuestionsSections/QuestionsSections"));
const TextOnlySection = dynamic(() => import("@/components/TextOnlySection/TextOnlySection"));
const HeadingSection = dynamic(() => import("@/components/HeadingSection/HeadingSection"));
const FeaturedLogosSection = dynamic(() => import("@/components/FeaturedLogosSection/FeaturedLogosSection"));
const DeepPlaneFaceliftSection = dynamic(() => import("@/components/DeepPlaneFaceliftSection/DeepPlaneFaceliftSection"));
const ReasonsBlepharoplasty = dynamic(() => import("@/components/ReasonsBlepharoplasty/ReasonsBlepharoplasty"));
const BlogPostSection = dynamic(() => import("@/components/Blog/BlogPostSection/BlogPostSection"));
const BlogIndexSection = dynamic(() => import("@/components/Blog/BlogIndexSection/BlogIndexSection"));
const BlogCategoryListSection = dynamic(() => import("@/components/Blog/BlogCategoryListSection/BlogCategoryListSection"));
const BlogTagListSection = dynamic(() => import("@/components/Blog/BlogTagListSection/BlogTagListSection"));
const BlogAuthorListSection = dynamic(() => import("@/components/Blog/BlogAuthorListSection/BlogAuthorListSection"));
const BlogSearchListSection = dynamic(() => import("@/components/Blog/BlogSearchListSection/BlogSearchListSection"), { ssr: false });
const ProcedureHeader = dynamic(() => import("@/components/Gallery/ProcedureHeader/ProcedureHeader"));
// const PatientSection = dynamic(() => import("@/components/Gallery/PatientSection/PatientSection"));
const PatientSection = dynamic(() => import("@/components/Gallery/PatientSection/PatientSectionRouter"));
const LatestFacelift = dynamic(() => import("@/components/LatestFacelift/LatestFacelift"));
const PatientExperience = dynamic(() => import("@/components/PatientExperience/PatientExperience"));
const AreYouReady = dynamic(() => import("@/components/AreYouReady/AreYouReady"));
const TextGray = dynamic(() => import("@/components/TextGray/TextGray"));
const MediaGalleryVideo = dynamic(() => import("@/components/MediaGalleryVideo/MediaGalleryVideo"));
const MedicalPublications = dynamic(() => import("@/components/MedicalPublications/MedicalPublications"));
const ContentSplitTextMap = dynamic(() => import("@/components/ContentSplitTextMap/ContentSplitTextMap"));
const ContentGridMagazine = dynamic(() => import("@/components/ContentGridMagazine/ContentGridMagazine"));
const ContentLeftDark = dynamic(() => import("@/components/ContentLeftDark/ContentLeftDark"));
const ContentTextLeftImageBg = dynamic(() => import("@/components/ContentTextLeftImageBg/ContentTextLeftImageBg"));
const ContentSplitLeftImageWhite = dynamic(() => import("@/components/ContentSplitLeftImageWhite/ContentSplitLeftImageWhite"));
const ProfileAboutSplitGrey = dynamic(() => import("@/components/ProfileAboutSplitGrey/ProfileAboutSplitGrey"));
const ContentSplitTextOnlyBlack = dynamic(() => import("@/components/ContentSplitTextOnlyBlack/ContentSplitTextOnlyBlack"));
const ContentSplitTextOnly = dynamic(() => import("@/components/ContentSplitTextOnly/ContentSplitTextOnly"));
const ContentSplitTextImage = dynamic(() => import("@/components/ContentSplitTextImage/ContentSplitTextImage"));
const AboutTheAuthorWhite = dynamic(() => import("@/components/AboutTheAuthorWhite/AboutTheAuthorWhite"));
const GridImageWithBtn = dynamic(() => import("@/components/GridImageWithBtn/GridImageWithBtn"));
const BookAConsultation = dynamic(() => import("@/components/BookAConsultation/BookAConsultationNext"));
const CaseStudySection = dynamic(() => import("@/components/CaseStudySection/CaseStudySection"));
const ResolutionOfBruising = dynamic(() => import("@/components/ResolutionOfBruising/ResolutionOfBruising"));
const SectionAnchor = dynamic(() => import("@/components/SectionAnchor/SectionAnchor"));
const BlogArchiveListSection = dynamic(() => import("@/components/Blog/BlogArchiveListSection/BlogArchiveListSection"));
const BgRightTxtScroll = dynamic(() => import("@/components/BgRightTxtScroll/BgRightTxtScroll"));
const AboutSectionRigthVideo = dynamic(() => import("@/components/AboutSectionRigthVideo/AboutSectionRigthVideo"));
const AboutSectionRigthVideoButtonsArea = dynamic(() => import("@/components/AboutSectionRigthVideoButtonsArea/AboutSectionRigthVideoButtonsArea"));
const SecExpect = dynamic(() => import("@/components/SecExpect/SecExpect"));
const SecCombine = dynamic(() => import("@/components/SecCombine/SecCombine"));
const SecWhatCanTreat = dynamic(() => import("@/components/SecWhatCanTreat/SecWhatCanTreat"));
const SecWhyChoose = dynamic(() => import("@/components/SecWhyChoose/SecWhyChoose"));
const SecWhatSets = dynamic(() => import("@/components/SecWhatSets/SecWhatSets"));
const SecTwoColGray = dynamic(() => import("@/components/SecTwoColGray/SecTwoColGray"));
const ProcedureAtGlance = dynamic(() => import("@/components/ProcedureAtGlance/ProcedureAtGlance"));
const WhyChooseUs = dynamic(() => import("@/components/WhyChooseUs/WhyChooseUs"));
const Method = dynamic(() => import("@/components/Method/Method"));
const WhatSetsApart = dynamic(() => import("@/components/WhatSetsApart/WhatSetsApart"));
const ProcessGuide = dynamic(() => import("@/components/ProcessGuide/ProcessGuide"));
const ServiceTypes = dynamic(() => import("@/components/ServiceTypes/ServiceTypes"));
const HomeAwards = dynamic(() => import("@/components/HomeAwards/HomeAwards"));
const ImageSplitDarkText = dynamic(() => import("@/components/ImageSplitDarkText/ImageSplitDarkText"));
const ImageWithCaptionText = dynamic(() => import("@/components/ImageWithCaptionText/ImageWithCaptionText"));
const RecoveryTimeline = dynamic(() => import("@/components/RecoveryTimeline/RecoveryTimeline"));
const ThreeColumnProceduresImages = dynamic(() => import("@/components/ThreeColumnProceduresImages/ThreeColumnProceduresImages"));
const GetInTouch = dynamic(() => import("@/components/GetInTouch/GetInTouch"));
const WrittenBy = dynamic(() => import("@/components/WrittenBy/WrittenBy"));
const TableOfContents = dynamic(() => import("@/components/TableOfContents/TableOfContents"));
const LandingPage = dynamic(() => import("@/components/LandingPage/LandingPage"));
const BridgeGrid = dynamic(() => import("@/components/BridgeGrid/BridgeGrid"));
const ContactUsPage = dynamic(() => import("@/components/ContactUsPage/ContactUsPage"));
const ConsultationFormPage = dynamic(() => import("@/components/ConsultationFormPage/ConsultationFormPage"));

// Dynamic — browser-only (ssr: false)
const GalleryIndex = dynamic(() => import("@/components/Gallery/GalleryIndex/GalleryIndex"), { ssr: false });
// const GalleryIndex = dynamic(() => import("@/components/Gallery/GalleryIndex/GalleryIndexV2"), { ssr: false }); // new design (temporary disabled)
// const ProcedureSection = dynamic(() => import("@/components/Gallery/ProcedureSection/ProcedureSection"), { ssr: false });
const ProcedureSection = dynamic(() => import("@/components/Gallery/ProcedureSection/ProcedureSectionRouter"), { ssr: false });
const GalleryMasonry = dynamic(() => import("@/components/GalleryMasonry/GalleryMasonry"), { ssr: false });
const GalleryGrid = dynamic(() => import("@/components/GalleryGrid/GalleryGrid"), { ssr: false });
const TestimonialsTextCarousel = dynamic(() => import("@/components/TestimonialsTextCarousel/TestimonialsTextCarousel"), { ssr: false });
const Insta = dynamic(() => import("@/components/Insta/Insta"), { ssr: false });
const GoogleReviews = dynamic(() => import("@/components/GoogleReviews/GoogleReviews"), { ssr: false });
const ReviewsWidget = dynamic(() => import("@/components/ReviewsWidget/ReviewsWidget"), { ssr: false });
const ReviewsWidgetPages = dynamic(() => import("@/components/ReviewsWidgetPages/ReviewsWidgetPages"), { ssr: false });
const VideoGrid = dynamic(() => import("@/components/VideoGrid/VideoGrid"), { ssr: false });
const VideoGridBlack = dynamic(() => import("@/components/VideoGridBlack/VideoGridBlack"), { ssr: false });
const VideosVFR7Results = dynamic(() => import("@/components/VideosVFR7Results/VideosVFR7Results"), { ssr: false });
const ClientResult = dynamic(() => import("@/components/ClientResult/ClientResult"), { ssr: false });
const VideoTabs = dynamic(() => import("@/components/VideoTabs/VideoTabs"), { ssr: false });
const HomeVideoTabs = dynamic(() => import("@/components/HomeVideoTabs/HomeVideoTabs"), { ssr: false });
const VideoTabsGrid = dynamic(() => import("@/components/VideoTabsGrid/VideoTabsGrid"), { ssr: false });
const ContentSplitLeftVideoWhite = dynamic(() => import("@/components/ContentSplitLeftVideoWhite/ContentSplitLeftVideoWhite"), { ssr: false });
const PartnerLogosSpin = dynamic(() => import("@/components/PartnerLogosSpin/PartnerLogosSpin"), { ssr: false });
const HomeGallery = dynamic(() => import("@/components/HomeGallery/HomeGallery"), { ssr: false });
const VideoDiaries = dynamic(() => import("@/components/VideoDiaries/VideoDiaries"), { ssr: false });
const TextLeftVideoSlider = dynamic(() => import("@/components/TextLeftVideoSlider/TextLeftVideoSlider"), { ssr: false });



type CompWithData = ({ data }: { data: any }) => JSX.Element;

const componentMap: Record<string, CompWithData> = {
	header: Header,
	footer: Footer,

	cmp_hero_main: HeroTitleTextCta,
	cmp_profile_about_links: AboutNavigationSection,
	cmp_profile_about_split: AboutSection,
	cmp_content_grid_4_col: FournColumnsSection,
	cmp_content_split_2_col_list: DrDeSilvaOverviewSection,
	cmp_media_banner: FeaturedLogosSection,
	cmp_partner_logos: MedicalAssociationsSection,
	cmp_content_split_right_image: ArtistrySection,
	cmp_artistry_backed_by_science: ArtistryBackedByScience,
	cmp_philanthropy_foundation: PhilanthropyFoundation,
	cmp_book_private_consultation: BookPrivateConsultation,
	cmp_content_split_left_image: PhilanthropySection,
	cmp_content_accordion: SpecialtyTrainingSection,
	cmp_content_hero_media: ConsultationCTA,
	cmp_content_text_centered_white: TestimonialSection,
	cmp_content_text_centered_black: AboutDrDeSilvaSection,
	cmp_content_text_centered_grey: TestimonialSectionGrey,
	cmp_content_split_text_media: DeepPlaneFaceliftSection,
	cmp_media_publications: MedicalPublications,
	cmp_gallery_grid: GalleryGrid,
	cmp_content_split_text_map: ContentSplitTextMap,
	cmp_media_gallery_video: MediaGalleryVideo,
	cmp_content_grid_magazine: ContentGridMagazine,
	cmp_video_grid: VideoGrid,
	cmp_gallery_masonry: GalleryMasonry,
	cmp_hero_main_split: HarleyStreetFaceliftHero,
	cmp_content_split_text_video: ReasonsBlepharoplasty,
	cmp_content_media_slider: InThePress,
	cmp_content_text_split_grey: CandidateDeepPlane,
	cmp_faq_accordion: FrequentlyQuestions,
	cmp_gallery_slider: FacialSurgeryResultsShowcase,
	cmp_content_split_30_70_left_image_white: AboutTheAuthorWhite,
	cmp_video_grid_black: VideoGridBlack,
	cmp_patient_experience: PatientExperience,
	cmp_video_results: VideosVFR7Results,
	cmp_single_video: ClientResult,
	cmp_content_split_image_covers: AwardsAndRecognitionSection,
	cmp_split_text_2_col_strict: VfrSevenFaceliftSteps,
	cmp_hero_left: HomeHero,
	text_only_section: TextOnlySection,
	cmp_content_split_left_button: WelcomeToPractice,
	cmp_content_split_profile_intro: FacialSurgeon,
	cmp_academic_published_expertise: AcademicPublishedExpertise,
	cmp_education_qualification_leadership: EducationQualificationLeadership,
	cmp_content_grid_procedures: WhatWeOffer,
	cmp_content_split_text_buttons: VerticalFaceRestore7,
	cmp_content_blog_preview: BlogPreview,
	cmp_grid_image_with_btn: GridImageWithBtn,
	cmp_testimonial_section: PatientsSaying,
	cmp_faq_questions: QuestionsSections,
	cmp_social_feed: Insta,
	cmp_google_reviews_widget: GoogleReviews,
	cmp_profile_about_split_grey: ProfileAboutSplitGrey,
	cmp_latest_facelift: LatestFacelift,
	cmp_are_you_ready: AreYouReady,
	cmp_heading_link_section: HeadingLinkSection,
	cmp_table: DeepPlaneVsStandard,
	cmp_content_left_dark: ContentLeftDark,
	cmp_partner_logos_spin: PartnerLogosSpin,
	cmp_content_text_left_image_bg: ContentTextLeftImageBg,
	cmp_content_split_left_video_white: ContentSplitLeftVideoWhite,
	cmp_content_split_text_only_black: ContentSplitTextOnlyBlack,
	cmp_nav_anchor: PageSectionNavigation,
	anchor: SectionAnchor,
	heading_section: HeadingSection,
	cmp_content_split_left_image_white: ContentSplitLeftImageWhite,

	cmp_resolution_of_bruising: ResolutionOfBruising,
	cmp_case_study_section: CaseStudySection,
	cmp_content_split_text_image: ContentSplitTextImage,

	cmp_content_split_30_70_left_image_black: AboutTheAuthor,
	cmp_text_gray: TextGray,
	cmp_content_split_text_only: ContentSplitTextOnly,

	cmp_book_a_consultation: BookAConsultation,
	cmp_inline_form: RequestCall,
	cmp_form_interested_split: InterestedInForm,
	cmp_form_consultation_split_image: ConsultationForm,

	cmp_bg_right_txt_scroll: BgRightTxtScroll,
	cmp_profile_about_split_right_video: AboutSectionRigthVideo,
	cmp_profile_about_split_right_video_buttons_area: AboutSectionRigthVideoButtonsArea,
	cmp_expect_section: SecExpect,
	cmp_combine_section: SecCombine,
	cmp_what_can_treat: SecWhatCanTreat,
	cmp_why_choose: SecWhyChoose,
	cmp_what_sets: SecWhatSets,
	cmp_two_col_gray: SecTwoColGray,
	// hidden
	//procedure_header: ProcedureHeader,
	// what_is_deep_plane_facelift: WhatIsDeepPlaneFacelift,
	//facial_layers_section: FacialLayersSection,
	// vfr7_deep_plane_facelift: Vfr7DeepPlaneFacelift,
	// what_to_expect: WhatToExpect,
	// title_button_section: TitleButtonSection,

	blog_post_section: BlogPostSection,
	blog_index_list_section: BlogIndexSection,
	blog_category_list_section: BlogCategoryListSection,
	blog_tag_list_section: BlogTagListSection,
	blog_author_list_section: BlogAuthorListSection,
	blog_search_list_section: BlogSearchListSection,
	blog_archive_list_section: BlogArchiveListSection,

	gallery_root: GalleryIndex,
	gallery_procedure: ProcedureSection,
	gallery_patient: PatientSection,

	cmp_testimonials_text_carousel: TestimonialsTextCarousel,
	cmp_video_diaries: VideoDiaries,
	cmp_hero_main_text: HeroMainText,
	cmp_reviews_widget: ReviewsWidget,
//new components
	cmp_procedure_at_glance: ProcedureAtGlance,
	cmp_why_choose_us: WhyChooseUs,
	cmp_text_left_video_slider: TextLeftVideoSlider,
	cmp_method: Method,
	cmp_what_sets_apart: WhatSetsApart,
	cmp_process_guide: ProcessGuide,
	cmp_reviews_widget_pages: ReviewsWidgetPages,
	cmp_service_types: ServiceTypes,
	cmp_video_tabs: VideoTabs,
	cmp_home_video_tabs: HomeVideoTabs,
	cmp_home_gallery: HomeGallery,
	cmp_home_awards: HomeAwards,
	cmp_video_tabs_grid: VideoTabsGrid,
	cmp_image_split_dark_text: ImageSplitDarkText,
	cmp_image_with_caption_text: ImageWithCaptionText,
	cmp_recovery_timeline: RecoveryTimeline,
	cmp_three_column_procedures_images: ThreeColumnProceduresImages,
	cmp_get_in_touch: GetInTouch,
	cmp_written_by: WrittenBy,
	table_of_contents: TableOfContents,
	landing_page: LandingPage,
	cmp_bridge_grid: BridgeGrid,
	cmp_contact_us_page: ContactUsPage,
	cmp_consultation_form: ConsultationFormPage,
}

const renderComponents = (
  components: PageComponent[] = [],
  componentData: ComponentData
) => {
  return components.map((component: PageComponent, index: number) => {
    const Component = componentMap[component.collection];
    if (!Component) {
      console.warn(
        `Component not found for collection: ${component.collection}`
      );
      return null;
    }

    if (component.item === "root_data") {
      return (
        <Fragment key={`${component.collection}-${index}`}>
          {componentData[component.collection]?.root_data?.map0 && (
            <h2 className="text-3xl font-bold mb-8 text-center">
              {component.collection}
            </h2>
          )}
          <Component
            data={componentData[component.collection]?.root_data}
          />
        </Fragment>
      );
    }

    const data = componentData[component.collection]?.[component.item];
    return (
      <Fragment key={`${component.collection}-${index}`}>
        {data?.map0 && (
          <h2 className="text-3xl font-bold mb-8 text-left bg-gray-600 text-white p-4">
            {component.collection}
          </h2>
        )}
        <Component data={data} />
      </Fragment>
    );
  });
};


const GALLERY_MAIN_COMPONENTS = ['gallery_root', 'gallery_procedure', 'gallery_patient'];
const EXCLUDED_FROM_GALLERY = [
  'cmp_hero_main', 'cmp_hero_main_split', 'cmp_hero_left', 'cmp_hero_main_text',
  'cmp_content_text_centered_white', 'cmp_content_text_centered_grey',
  'cmp_testimonial_section', 'cmp_testimonials_text_carousel',
  'heading_section',
];

export default function DynamicPage({ page }: PageProps) {
  if (!page) return null;

  const isGalleryPage = typeof page.template === "object" &&
    GALLERY_MAIN_COMPONENTS.includes(page.template?.main_component ?? '');

  const pageComponents = useMemo(
    () =>
      page.page_components
        ? page.page_components.map((component, index) => ({
            ...component,
            key: `page-${component.collection}-${index}`,
          }))
        : [],
    [page.page_components]
  );

  return (
    <>
      <LoadingBar />
      <HtmlLinksLoader />

      {typeof page.template === "object" &&
        page.template?.head_components &&
        renderComponents(
          page.template.head_components
            .filter((c: PageComponent) => !(isGalleryPage && EXCLUDED_FROM_GALLERY.includes(c.collection)))
            .map((component: PageComponent, index: number) => ({
              ...component,
              key: `head-${component.collection}-${index}`
            })),
          page.componentData || {}
        )}

      {/* Site health #9: <main> landmark around the page content */}
      <main>
        {page.page_components ? (
          renderComponents(pageComponents, page.componentData || {})
        ) : (
          typeof page.template === "object" &&
          page.template?.main_component &&
          renderComponents(
            [
              {
                collection: page.template.main_component,
                item: "root_data",
                key: `main-${page.template.main_component}`,
              },
            ],
            page.componentData || {}
          )
        )}
      </main>

      {typeof page.template === "object" &&
        page.template?.foot_components &&
        renderComponents(
          page.template.foot_components.map((component, index) => ({
            ...component,
            key: `foot-${component.collection}-${index}`
          })),
          page.componentData || {}
        )}
    </>
  );
}

// Add this test data structure
const testPages = [
	{
		slug: 'test',
		title: 'Home Page',
		description: 'Welcome to our site',
		page_components: [],
		template: {
			// head_components: [
			//   {
			//     collection: "header",
			//     item: "header",
			//   },
			// ],
		},
		componentData: {},
		seo: {
			title: 'Test Page',
			description: 'This is a test page for development',
		},
	},
]

async function getNewContactUsPageProps(
  slug: string,
  propsBase: { preview: boolean },
  options?: {
    title?: string
    status?: string
    robotsNoindex?: boolean
    revalidate?: number | false
  }
) {
  const basePage = await withTiming(`pageService.getBySlug(contact-us for ${slug})`, () =>
    pageService.getBySlug('contact-us')
  )
  if (!basePage) return { notFound: true as const, revalidate: NOT_FOUND_REVALIDATE_SECONDS }

  const pageComponents: PageComponent[] = [
    { collection: 'cmp_contact_us_page', item: '1', order: 1 },
  ]

  const template = typeof basePage.template === 'object' ? basePage.template : undefined
  const allComponents = [
    ...(template?.head_components || []),
    ...pageComponents,
    ...(template?.foot_components || []),
  ]

  const componentData = await withTiming(`componentService.getComponentData(${slug})`, () =>
    componentService.getComponentData(allComponents, undefined, {
      strict: true,
      ignoreCollections: ['cmp_contact_us_page'],
    })
  )

  if (!componentData.cmp_contact_us_page) {
    componentData.cmp_contact_us_page = { '1': {} }
  }

  const page: Page & { robotsNoindex?: boolean } = {
    ...basePage,
    slug,
    page_components: pageComponents,
    componentData,
    ...(options?.title ? { title: options.title } : {}),
    ...(options?.status ? { status: options.status } : {}),
    ...(options?.robotsNoindex !== undefined ? { robotsNoindex: options.robotsNoindex } : {}),
  }

  return {
    props: { page, ...propsBase },
    revalidate: options?.revalidate ?? false,
  }
}

const STATIC_SLUGS = ['contact-us', 'contact-us-test', 'pre-consultation-intake', 'pre-consultation-form'];

const NOINDEX_SLUGS = ['thank-you'];

export const getStaticPaths: GetStaticPaths = async () => {
  const pageSlugs = await pageService.getPublishedPageSlugs();
  const slugs = Array.from(new Set([...STATIC_SLUGS, ...pageSlugs]));

  return {
    paths: slugs.map((slug) => ({
      params: { slug: slug === 'index' ? [] : slug.split('/') },
    })),
    fallback: 'blocking',
  };
};

const getPageStaticProps: GetStaticProps = async ({ params, preview = false }) => {
  const slugParts = (params?.slug as string[]) || [];
  const propsBase = { preview };
  const requestedSlug = !params?.slug
    ? 'index'
    : Array.isArray(params.slug)
      ? params.slug.join('/') || 'index'
      : (params.slug as string);

  if (slugParts[0] === 'api') return { notFound: true };

  // Blog index + pagination
  if (
    (slugParts[0] === process.env.NEXT_PUBLIC_BLOG_SLUG && !slugParts[1]) ||
    (slugParts[0] === process.env.NEXT_PUBLIC_BLOG_SLUG && slugParts[1] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG && slugParts[2])
  ) {
    const currentPage = slugParts[1] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG ? parseInt(slugParts[2]) : 1;
    const page = await withTiming('blogService.getBlogPosts', () => blogService.getBlogPosts(currentPage));
    // Site health #10: paginated blog listing pages (/page/N/) are noindex, follow
    const isPaginated = slugParts[1] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG;
    if (page) {
      return {
        props: { page: isPaginated ? { ...page, robotsNoindex: true, robotsFollow: true } : page, ...propsBase },
        revalidate: PAGE_REVALIDATE_SECONDS,
      };
    }
  }

  // Blog categories
  if (
    slugParts[0] === process.env.NEXT_PUBLIC_BLOG_SLUG &&
    slugParts[1] === process.env.NEXT_PUBLIC_BLOG_CATEGORY_SLUG &&
    slugParts[2]
  ) {
    const currentPage = slugParts[3] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG ? parseInt(slugParts[4]) : 1;
    const page = await withTiming('blogService.getBlogPostsByCategory', () =>
      blogService.getBlogPostsByCategory(slugParts[2], currentPage)
    );
    if (page) return { props: { page: { ...page, robotsNoindex: true, robotsFollow: true }, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
  }

  // Blog tags
  if (
    slugParts[0] === process.env.NEXT_PUBLIC_BLOG_SLUG &&
    slugParts[1] === process.env.NEXT_PUBLIC_BLOG_TAG_SLUG &&
    slugParts[2]
  ) {
    const currentPage = slugParts[3] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG ? parseInt(slugParts[4]) : 1;
    const page = await withTiming('blogService.getBlogPostsByTag', () =>
      blogService.getBlogPostsByTag(slugParts[2], currentPage)
    );
    if (page) return { props: { page, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
  }

  // Blog authors
  if (
    slugParts[0] === process.env.NEXT_PUBLIC_BLOG_SLUG &&
    slugParts[1] === process.env.NEXT_PUBLIC_BLOG_AUTHOR_SLUG &&
    slugParts[2]
  ) {
    const currentPage = slugParts[3] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG ? parseInt(slugParts[4]) : 1;
    const page = await withTiming('blogService.getBlogPostsByAuthor', () =>
      blogService.getBlogPostsByAuthor(slugParts[2], currentPage)
    );
    if (page) return { props: { page, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
  }

  // Blog archive — must be before single post check
  if (
    slugParts[0] === process.env.NEXT_PUBLIC_BLOG_SLUG &&
    slugParts[1] === process.env.NEXT_PUBLIC_BLOG_ARCHIVE_SLUG &&
    slugParts[2]
  ) {
    const currentPage = slugParts[3] === process.env.NEXT_PUBLIC_BLOG_PAGE_SLUG ? parseInt(slugParts[4]) : 1;
    const page = await withTiming('blogService.getBlogPostsByArchive', () =>
      blogService.getBlogPostsByArchive(slugParts[2], currentPage)
    );
    if (page) return { props: { page: { ...page, robotsNoindex: true, robotsFollow: true }, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
  }

  // Blog single post
  if (slugParts[0] === process.env.NEXT_PUBLIC_BLOG_SLUG && slugParts[1]) {
    const page = await withTiming('blogService.getBlogPost', () => blogService.getBlogPost(slugParts[1]));
    if (page) return { props: { page, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
  }

  // Gallery index
  if (slugParts[0] === process.env.NEXT_PUBLIC_GALLERY_SLUG && !slugParts[1]) {
    const page = await withTiming('galleryService.getGalleryCategories', () => galleryService.getGalleryCategories());
    if (page) return { props: { page, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
  }

  // Gallery procedure
  if (slugParts[0] === process.env.NEXT_PUBLIC_GALLERY_SLUG && slugParts[1] && !slugParts[2]) {
    const page = await withTiming('galleryService.getGalleryProcedures', () => galleryService.getGalleryProcedures(slugParts[1]));
    if (page) return { props: { page, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
  }

  // Gallery patient
  if (
    slugParts[0] === process.env.NEXT_PUBLIC_GALLERY_SLUG &&
    slugParts[1] &&
    slugParts[2] === process.env.NEXT_PUBLIC_GALLERY_PATIENT_SLUG &&
    slugParts[3]
  ) {
    const page = await withTiming('galleryService.getGalleryPatients', () => galleryService.getGalleryPatients(slugParts[1], slugParts[3]));
    if (page) return { props: { page, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
  }

  // Components map
  if (slugParts[0] === process.env.NEXT_PUBLIC_COMPONENTS_MAP_SLUG && !slugParts[1]) {
    const page = await withTiming('componentService.getComponentsMap', () => componentService.getComponentsMap(componentMap));
    if (page) return { props: { page, ...propsBase }, revalidate: false };
  }

  // Dev test pages
  if (process.env.ENVIRONMENT === 'development') {
    const page = testPages.find((p) => p.slug === requestedSlug);
    if (page) return { props: { page, ...propsBase }, revalidate: false };
  }

  if (requestedSlug === 'contact-us') {
    return getNewContactUsPageProps('contact-us', propsBase, { revalidate: PAGE_REVALIDATE_SECONDS })
  }

  if (requestedSlug === 'contact-us-test') {
    return getNewContactUsPageProps('contact-us-test', propsBase, {
      title: 'Contact Us | Dr Julian De Silva',
      status: 'forapproval',
      robotsNoindex: true,
      revalidate: false,
    })
  }

  if (requestedSlug === 'pre-consultation-intake' || requestedSlug === 'pre-consultation-form') {
    const basePage = await withTiming('pageService.getBySlug(contact-us)', () =>
      pageService.getBySlug('contact-us')
    );
    if (!basePage) return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };

    const pageComponents: PageComponent[] = [
      { collection: 'cmp_consultation_form', item: '1', order: 1 },
    ];

    const allComponents = [
      ...((typeof basePage.template === 'object' && basePage.template?.head_components) || []),
      ...pageComponents,
      ...((typeof basePage.template === 'object' && basePage.template?.foot_components) || []),
    ];

    const componentData = await withTiming('componentService.getComponentData(pre-consultation-intake)', () =>
      componentService.getComponentData(allComponents, undefined, { strict: true })
    );

    if (!componentData.cmp_consultation_form) {
      componentData.cmp_consultation_form = { '1': {} };
    }

    const page: Page = {
      ...basePage,
      slug: requestedSlug,
      title: 'Consultation Request | Dr Julian De Silva',
      status: 'forapproval',
      robotsNoindex: true,
      page_components: pageComponents,
      componentData,
      seo: [],
    };

    return { props: { page, ...propsBase }, revalidate: false };
  }

  // Regular content pages
  const page = await withTiming('pageService.getBySlug', () => pageService.getBySlug(requestedSlug));
  if (!page) return { notFound: true, revalidate: NOT_FOUND_REVALIDATE_SECONDS };

  if (NOINDEX_SLUGS.includes(requestedSlug)) {
    return {
      props: { page: { ...page, robotsNoindex: true, robotsFollow: true }, ...propsBase },
      revalidate: PAGE_REVALIDATE_SECONDS,
    };
  }

  return { props: { page, ...propsBase }, revalidate: PAGE_REVALIDATE_SECONDS };
};

// Site health #2: template.seo duplicates page.seo and is not used by the client, so it is not sent in __NEXT_DATA__
export const getStaticProps: GetStaticProps = async (context) =>
  removeDuplicatedTemplateSeo(await getPageStaticProps(context));
